/**
 * File: /Users/tomasmuniesa/Documents/develop/nousmedis/nm-apps/3_catalog_cloud/develop/frontend/src/app/modules/catalog-cloud/services/fascicle-socket.service.ts
 * Project: /Users/tomasmuniesa/Documents/develop/nousmedis/nm-apps/3_catalog_cloud/develop/frontend
 * Created Date: Sunday, March 15th 2026, 11:57:07 pm
 * Author: Tomás
 * -----
 * Last Modified:  Sunday, 15th March 2026 11:57:10 pm
 * Modified By: Tomás (tomas@cege.es>)
 * -----
 * Copyright (c) 2026 Nousmedis, CeGe
 * ------------------------------------
 */


/*
 * File: socket.service.ts                                                     *
 * Project: catalog-cloud                                                      *
 * File Created: Tuesday, 1st February 2022 21:34:49                           *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Thursday, 2nd March 2023 14:40:19                            *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { io, Socket, Manager } from 'socket.io-client';

import { environment } from 'environment';
import { FascicleAskUnblockComponent } from '../components/fascicle/fascicle-ask-unblock/fascicle-ask-unblock.component';
import { CatalogService } from '../../../modules/catalog-cloud/services/catalog.service';
import { UserService } from '../../user/services/user.service';
import { FascicleService } from './fascicle.service';
import { ConfirmationService, MessageService } from 'primeng/api';

enum action {
  connect = 'connect',
  unblockQuest = 'unblock_question',
  sendMessage = 'send-message',
  getMessage = 'get-message',
  changeFascicle = 'change-fascicle',
  takeOverFascicle = 'take-over-fascicle'
}

enum key {
  userId = 'user-id',
  unblockRequest = 'unblock-request',
  unblockResponse = 'unblock-response',
}

/** Datos de usuario incluidos en los eventos de bloqueo/desbloqueo de fascículos. */
export interface FascicleBlockUser {
  id: string;
  name: string;
  email?: string;
}

/** Payload que viaja en el evento socket `unblock_question`. */
export interface FascicleUnblockRequestData {
  catalogId: string;
  fascicleId: string;
  /** Usuario que solicita el desbloqueo. */
  claimantId: string;
  /** Nombre legible del solicitante, enviado por el cliente al emitir la petición. */
  claimantName?: string;
  /** Usuario que tiene el fascículo bloqueado. El backend enriquece este campo
   *  con el objeto completo al reenviar el evento, por lo que en el listener
   *  siempre llegará como objeto { id, name, email? }. */
  blockedBy: FascicleBlockUser;
}

@Injectable({
  providedIn: 'root',
})
export class FascicleSocketService {
  private socket: Socket;
  private manager: Manager;
  fasciclesChanged: Subject<boolean> = new Subject<boolean>();
  fascicleToTakeOver: Subject<{ catalogId: string; fascicleId: string; userId: string }> = new Subject();
  unblockResult: Subject<{ accepted: boolean; catalogId: string; fascicleId: string }> = new Subject();
  forceNavigate: Subject<{ catalogId: string; fascicleId: string }> = new Subject();

  constructor (
    private userService: UserService,
    private translate: TranslateService,
    private fascicleService: FascicleService,
    private catalogService: CatalogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: Router
  ) {
    const token = this.userService.getToken();
    const baseSocket = sessionStorage.getItem("base_socket") || "^^no-base-socket-server^^"; //dev http://localhost:3300
    this.manager = new Manager(baseSocket, {
      extraHeaders: {
        'api-key': `Bearer ${token}`,
      }
    });

    this.socket = this.manager.socket('/');
  }

  startSocket(userId: string) {
    this.listenForUser(userId);
    this.listenForUnblockRequest();
    this.listenForChangeFascicle();
    this.listenForTakeOver();
    this.listenForUnblockResult();
    this.listenForForceNavigate();
  }

  private listenForTakeOver() {
    this.socket.on(action.takeOverFascicle, (data: { catalogId: string; fascicleId: string; userId: string }) => {
      this.fascicleToTakeOver.next(data);
    });
  }

  private listenForUnblockResult() {
    this.socket.on('unblock-result', (data: { accepted: boolean; catalogId: string; fascicleId: string }) => {
      this.unblockResult.next(data);
    });
  }

  private listenForForceNavigate() {
    this.socket.on('force-navigate', (data: { catalogId: string; fascicleId: string }) => {
      this.confirmationService.close();
      this.forceNavigate.next(data);
    });
  }

  private listenForChangeFascicle() {
    this.socket.on(action.changeFascicle, () => {
      this.fasciclesChanged.next(true);
    });
  }

  private listenForUser(userId: string) {
    this.socket.on(action.connect, () => {
      this.socket.emit(key.userId, userId);
    });

    this.socket.on(action.getMessage, (data) => {
      this.showMessage(data);
    });
  }

  private listenForUnblockRequest() {
    // El backend reenvía el evento con blockedBy como objeto { id, name, email? }.
    this.socket.on(action.unblockQuest, (data: FascicleUnblockRequestData) => {
      if (data.blockedBy.id === this.userService.getCurrentUser().id) {
        this.openUnblockRequest(data);
      }
    });
  }

  private openUnblockRequest(data: FascicleUnblockRequestData) {
    const requesterName = data.claimantName ?? data.claimantId;
    let myuser = this.userService.getCurrentUser();
    this.catalogService.getCatalog(data.catalogId).subscribe((catalog) => {
      const fascicleName = catalog.fascicles.filter((f: any) => f.id === data.fascicleId)[0]?.name ?? data.fascicleId;

      if (myuser.id == data.blockedBy.id) {
        this.confirmationService.confirm({
          message: this.translate.instant('FASCICLE.UNBLOCK_REQUEST_MSG', { claimantName: requesterName, fascicleName }),
          header: this.translate.instant('FASCICLE.UNBLOCK_REQUEST_HEADER'),
          closeOnEscape: true,
          icon: 'pi pi-lock-open',
          acceptLabel: this.translate.instant('FASCICLE.UNBLOCK_REQUEST_ACCEPT'),
          acceptButtonStyleClass: 'p-button-sm',
          rejectLabel: this.translate.instant('FASCICLE.UNBLOCK_REQUEST_REJECT'),
          rejectButtonStyleClass: 'p-button-sm p-button-outlined p-button-secondary',
          accept: () => {
            this.sendUnblockResponse(data.catalogId, data.fascicleId, true);
            this.fascicleService.unblock(data.catalogId, data.fascicleId).subscribe(() => {
              this.catalogService.getCatalog(data.catalogId).subscribe((catalog) => {
                const catalogName = catalog.name;
                const fascicleName = catalog.fascicles.filter((f: any) => f.id === data.fascicleId)[0]?.name ?? data.fascicleId;
                const me = this.userService.getCurrentUser().name + ' ' + this.userService.getCurrentUser().surname;
                this.sendMessage(
                  this.translate.instant('FASCICLE.IS_UNBLOCKED', { fascicleName, catalogName, username: me }),
                  data.claimantId
                );
                this.sendChanges(catalog.idCatalog, data.fascicleId, this.userService.getCurrentUser().id, false);
                this.route.navigate(['/catalog', data.catalogId, 'fascicles']);
              });
            });
          },
          reject: () => {
            this.sendUnblockResponse(data.catalogId, data.fascicleId, false);
            this.catalogService.getCatalog(data.catalogId).subscribe((catalog) => {
              const catalogName = catalog.name;
              const fascicleName = catalog.fascicles.filter((f: any) => f.id === data.fascicleId)[0]?.name ?? data.fascicleId;
              const me = this.userService.getCurrentUser().name + ' ' + this.userService.getCurrentUser().surname;
              this.sendMessage(
                this.translate.instant('FASCICLE.IS_NOT_UNBLOCKED', { fascicleName, catalogName, username: me }),
                data.claimantId
              );
            });
          }
        });
      }
    });
  }

  private showMessage(data: any) {
    if (this.userService.getCurrentUser().id == data.idUserReceptor)
      this.messageService.add({
        severity: 'info',
        summary: 'Mensaje',
        detail: data.msg,
      });
  }

  sendTakeOver(catalogId: string, fascicleId: string, userId: string): void {
    this.socket.emit(action.takeOverFascicle, { catalogId, fascicleId, userId });
  }

  sendChanges(idCatalog: string, idFascicle: string, idUser: string, blocked: boolean) {
    this.socket.emit(action.changeFascicle, {idCatalog: idCatalog, idFascicle: idFascicle, idUser: idUser, blocked: blocked});
  }

  sendMessage(msg: string, idUser: string) {
    let emidData: any = {};
    emidData.msg = msg;
    emidData.idUserReceptor = idUser;
    this.socket.emit(action.sendMessage, emidData);
  }

  /**
   * Emite una solicitud de desbloqueo al usuario que tiene el fascículo bloqueado.
   *
   * @param catalogId    ID del catálogo al que pertenece el fascículo.
   * @param fascicleId   ID del fascículo bloqueado.
   * @param claimantId   ID del usuario que solicita el desbloqueo.
   * @param blockedBy    Objeto con los datos del usuario bloqueador. Si solo se
   *                     dispone del ID (p. ej. cuando `Fascicle.blockedBy` es
   *                     string), se puede pasar `{ id: blockedByString }` y el
   *                     backend enriquecerá el objeto al reenviar el evento.
   * @param claimantName Nombre legible del solicitante, para mostrarlo en el diálogo.
   */
  /** Notifica al backend que el usuario ha respondido al diálogo (acepta o rechaza),
   *  cancelando el timer de auto-desbloqueo de 10s. */
  sendUnblockResponse(catalogId: string, fascicleId: string, accepted: boolean): void {
    this.socket.emit(key.unblockResponse, { catalogId, fascicleId, accepted });
  }

  sendUnblockRequestFascicle(
    catalogId: string,
    fascicleId: string,
    claimantId: string,
    blockedBy: FascicleBlockUser | string,
    claimantName?: string,
  ) {
    // Normalizar: si el caller pasa un string legacy, convertirlo a objeto mínimo.
    const blockedByObj: FascicleBlockUser =
      typeof blockedBy === 'string'
        ? { id: blockedBy, name: '' }
        : blockedBy;

    const payload: FascicleUnblockRequestData = {
      catalogId,
      fascicleId,
      claimantId,
      claimantName,
      blockedBy: blockedByObj,
    };
    this.socket.emit(key.unblockRequest, payload);
  }
}
